import { PropertyDataResponse } from '@/ts/HococoInterfaces';

export default class PropertyData {
    public id: number;
    public address: string;
    public city: string;
    public zipCode: number;
    public floor: number;
    public type: string;
    public titleNumber: string;
    public manager: number;
    public managers: number[] = [];
    public tenants: number[] = [];
    public owner?: string;
    public directOwnerId?: number = null;
    public externalId: string;
    public ownerId: number = null;
    public managerName: string;
    public variantId: number;
    public countryId: number;
    public buildingId: number;

    public selected: boolean;
    public authorizedUsers: { [index: number]: number[] } = {};

    public companyNumber: string = null;
    public companyName: string = null;
    public isCommercial: boolean = false;
    public guestsEnabled: boolean = false;

    // Rent and Deposit
    public rentAmount: number = 0;
    public rentCurrency: string = 'DKK';

    public depositAmount: number = 0;
    public depositCurrency: string = 'DKK';

    public area: number;
    public number: string;
    public unitType: string;
    public moveInDate: string;
    public releaseDate: string;
    public vacantFrom: string;
    public futureRent: number;
    public heatingCost: number;
    public waterCost: number;
    public electricityCost: number;
    public prepaidRent: number;
    public internetCost: number;
    public ubTotal: number;

    public utilitiesAmount: number = 0.0;
    public rooms: number = 0;

    public projectName: string = '';

    constructor(
        id: number,
        address: string = '',
        city: string = '',
        zipCode: number = null,
        floor: number = null,
        type: string = '',
        variantId: number = null,
        countryId: number = null,
        buildingId: number = null,
        titleNumber: string = '',
        rentAmount: number = null,
        depositAmount: number = null,
        unitType: string = null,
        moveInDate: string = null,
        releaseDate: string = null,
        vacantFrom: string = null,
        futureRent: number = null,
        heatingCost: number = null,
        waterCost: number = null,
        electricityCost: number = null,
        prepaidRent: number = null,
        internetCost: number = null,
        ubTotal: number = null,
        area: number = null,
        directOwnerId: number = null,
        externalId: string = null,
        companyNumber: string = null,
        companyName: string = null,
        isCommercial: boolean = false,
        rooms: number = 0,
        projectName: string = '',
        guestsEnabled: boolean = false,
    ) {
        this.id              = id;
        this.address         = address;
        this.city            = city;
        this.zipCode         = zipCode;
        this.floor           = floor;
        this.type            = type;
        this.variantId       = variantId;
        this.countryId       = countryId;
        this.buildingId      = buildingId;
        this.titleNumber     = titleNumber;
        this.rentAmount      = rentAmount;
        this.depositAmount   = depositAmount;
        this.selected        = false;
        this.unitType        = unitType;
        this.moveInDate      = moveInDate;
        this.releaseDate     = releaseDate;
        this.vacantFrom      = vacantFrom;
        this.futureRent      = futureRent;
        this.heatingCost     = heatingCost;
        this.waterCost       = waterCost;
        this.electricityCost = electricityCost;
        this.prepaidRent     = prepaidRent;
        this.internetCost    = internetCost;
        this.ubTotal         = ubTotal;
        this.area            = area;
        this.directOwnerId   = directOwnerId;
        this.externalId      = externalId;
        this.companyNumber   = companyNumber;
        this.companyName     = companyName;
        this.isCommercial    = isCommercial;
        this.rooms           = rooms;
        this.projectName     = projectName;
        this.guestsEnabled   = guestsEnabled;

        this.calculateUtilities();
    }

    get persisted(): boolean {
        return (this.id !== -1);
    }

    public requireUpdate(propertyResponse: PropertyDataResponse): boolean {
       return (
           this.type !== propertyResponse.type
       );
    }

    public updateLocalPropertyData(propertyResponse: PropertyDataResponse): void {
        if (this.requireUpdate(propertyResponse)) {
            this.type = propertyResponse.type;
        }
    }

    public calculateUtilities() {
        this.utilitiesAmount = 0.0;

        if (this.heatingCost) {
            this.utilitiesAmount += parseFloat(this.heatingCost.toString());
        }

        if (this.electricityCost) {
            this.utilitiesAmount += parseFloat(this.electricityCost.toString());
        }

        if (this.waterCost) {
            this.utilitiesAmount += parseFloat(this.waterCost.toString());
        }
    }

    public populateRoleIds() {
        const managerRoleId = window.hoc.userRoleKeys['facility-manager'];
        const ownerRoleId = window.hoc.userRoleKeys['property-owner'];
        const tenantRoleId = window.hoc.userRoleKeys['property-tenant'];
        const managerResult: number[] = [];
        const tenantResult: number[] = [];

        for (const userId in this.authorizedUsers) {
            if (this.authorizedUsers.hasOwnProperty(userId)) {
                const parsedId = parseInt(userId, 10);

                if (this.authorizedUsers[userId].includes(managerRoleId)) {
                    managerResult.push(parsedId);
                }

                if (this.authorizedUsers[userId].includes(ownerRoleId) && parsedId === this.directOwnerId) {
                    tenantResult.push(parsedId);
                    this.ownerId = parsedId;
                }

                if (this.authorizedUsers[userId].includes(tenantRoleId)) {
                    tenantResult.push(parsedId);
                }
            }
        }

        this.tenants = tenantResult;
        this.managers = managerResult;
    }

    public getRequestData() {
        return {
            address: this.address,
            city: this.city,
            zipCode: this.zipCode,
            floor: this.floor,
            managers: this.managers,
            variantId: this.variantId,
            countryId: this.countryId,
            buildingId: this.buildingId,
            titleNumber: this.titleNumber,
            rentAmount: this.rentAmount,
            depositAmount: this.depositAmount,
            unitType: this.unitType,
            moveInDate: this.moveInDate,
            releaseDate: this.releaseDate,
            vacantFrom: this.vacantFrom,
            futureRent: this.futureRent,
            heatingCost: this.heatingCost,
            waterCost: this.waterCost,
            electricityCost: this.electricityCost,
            prepaidRent: this.prepaidRent,
            internetCost: this.internetCost,
            ubTotal: this.ubTotal,
            area: this.area,
            isCommercial: this.isCommercial,
            rooms: this.rooms,
        };
    }

    public dataEquals(property: PropertyData): boolean {
        // Check if this object's values are equal to the given object's
        if (
            this.id === property.id &&
            this.address === property.address &&
            this.city === property.city &&
            this.zipCode === property.zipCode &&
            this.floor === property.floor &&
            this.type === property.type &&
            this.owner === property.owner &&
            this.variantId === property.variantId &&
            this.countryId === property.countryId &&
            this.buildingId === property.buildingId &&
            this.titleNumber === property.titleNumber &&
            this.rentAmount === property.rentAmount &&
            this.depositAmount === property.depositAmount &&
            this.rentCurrency === property.rentCurrency &&
            this.depositCurrency === property.depositCurrency &&
            this.unitType === property.unitType &&
            this.moveInDate === property.moveInDate &&
            this.releaseDate === property.releaseDate &&
            this.vacantFrom === property.vacantFrom &&
            this.futureRent === property.futureRent &&
            this.heatingCost === property.heatingCost &&
            this.waterCost === property.waterCost &&
            this.electricityCost === property.electricityCost &&
            this.prepaidRent === property.prepaidRent &&
            this.internetCost === property.internetCost &&
            this.ubTotal === property.ubTotal &&
            this.area === property.area &&
            this.authEquals(property)
        ) {
            return false;
        }
    }

    public cloneData(property: PropertyData) {
        this.titleNumber     = property.titleNumber;
        this.address         = property.address;
        this.city            = property.city;
        this.area            = property.area;
        this.zipCode         = property.zipCode;
        this.floor           = property.floor;
        this.type            = property.type;
        this.unitType        = property.unitType;
        this.buildingId      = property.buildingId;
        this.countryId       = property.countryId;
        this.variantId       = property.variantId;
        this.directOwnerId   = property.directOwnerId;
        this.externalId      = property.externalId;
        this.ownerId         = property.ownerId;
        this.tenants         = property.tenants;
        this.depositAmount   = property.depositAmount;
        this.electricityCost = property.electricityCost;
        this.heatingCost     = property.heatingCost;
        this.internetCost    = property.internetCost;
        this.rentAmount      = property.rentAmount;
        this.waterCost       = property.waterCost;
        this.ubTotal         = property.ubTotal;
        this.depositCurrency = property.depositCurrency;
        this.rentCurrency    = property.rentCurrency;
        this.prepaidRent     = property.prepaidRent;
        this.futureRent      = property.futureRent;
        this.moveInDate      = property.moveInDate;
        this.releaseDate     = property.releaseDate;
        this.vacantFrom      = property.vacantFrom;
        this.managers        = property.managers;
        this.selected        = property.selected;
        this.authorizedUsers = property.authorizedUsers;
    }

    public addManager(userId: number) {
        this.addAuthorization(userId, window.hoc.userRoleKeys['facility-manager']);
    }

    public removeManager(userId: number) {
        this.removeAuthorization(userId, window.hoc.userRoleKeys['facility-manager']);
    }

    public addOwner(userId: number) {
        this.addAuthorization(userId, window.hoc.userRoleKeys['property-owner']);
    }

    public addAuthorization(userId: number, roleId: number) {

        if (typeof this.authorizedUsers[userId] === 'undefined') {
            this.authorizedUsers[userId] = [roleId];
        } else {
            if (this.authorizedUsers[userId].includes(roleId)) {
                return;
            }

            this.authorizedUsers[userId].push(roleId);
            this.authorizedUsers[userId].sort();
        }

        this.populateRoleIds();
    }

    public removeAuthorization(userId: number, roleId: number) {
        const index = this.authorizedUsers[userId].indexOf(roleId);

        if (index === -1) {
            return;
        }

        this.authorizedUsers[userId] = this.authorizedUsers[userId]
            .filter((role) => roleId !== roleId);

        this.authorizedUsers[userId].sort();

        this.populateRoleIds();
    }

    public evictTenants(): boolean {
        this.ownerId = null;
        this.directOwnerId = null;

        while (this.tenants.length > 0) {
            this.tenants.pop();
        }

        return true;
    }

    public emptyTenants(): boolean {
        this.tenants = [];

        return true;
    }

    public emptyAuthorizedUsers(): boolean {
        this.authorizedUsers = {};

        return true;
    }

    public updateCompanyData(data: any) {
        this.companyName   = data.companyName;
        this.companyNumber = data.companyNumber;
    }

    public updateOwner(ownerId: any) {
        this.ownerId = ownerId;
        this.directOwnerId = ownerId;
    }

    public removeOwner() {
        this.ownerId = null;
        this.directOwnerId = null;
    }

    private authEquals(auth: any): boolean {
        if (Object.keys(this.authorizedUsers).length !== Object.keys(auth).length) {
            return false;
        }

        const ownKeys = Object.keys(this.authorizedUsers).slice(0).sort();
        const theirKeys = Object.keys(auth).slice(0).sort();

        if (JSON.stringify(ownKeys) !== JSON.stringify(theirKeys)) {
            return false;
        }

        for (const userId in this.authorizedUsers) {
            if (JSON.stringify(this.authorizedUsers[userId]) !== JSON.stringify(auth[userId])) {
                return false;
            }
        }

        return true;
    }
}
